import React, { Component, createRef } from "react";
import { Link } from "react-router-dom";
import CESSVG from "../../Assets/svg/CESSVG.svg";
import ChangePasswordImage from "../../Assets/jpeg/OverLay.jpg";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouterHooks } from "../../withRouters/withRoutersHook";
import { AddForgotPasswordToOTPAction } from "../../Actions/Authentication";
import { ArrowLeft, KeyRound } from "lucide-react";

class VerifyConfirmPassword extends Component {
  state = {
    otp: "",
    formError: null,
  };

  static propTypes = {
    AddForgotPasswordToOTPAction: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    ForgotPasswordOtp: PropTypes.string,
    forgotPasswordEmail: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.inputsRef = Array(4).fill(0).map(() => createRef());
  }

  onSubmit = (e) => {
    e.preventDefault();
    const { otp } = this.state;

    if (!otp || otp.length < 4) {
      this.setState({ formError: "Please enter the complete 4-digit code." });
      return;
    }

    this.setState({ formError: null });
    this.props.AddForgotPasswordToOTPAction(otp);
  };

  handleInput = (e, index) => {
    const { value } = e.target;
    if (value && index < this.inputsRef.length - 1) {
      this.inputsRef[index + 1].current.focus();
    }
    let newOtp = this.state.otp.split("");
    newOtp[index] = value;
    this.setState({ otp: newOtp.join("") });
  };

  handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && index > 0 && !e.target.value) {
      this.inputsRef[index - 1].current.focus();
    }
  };

  componentDidUpdate(prevProps) {
    if (this.props.ForgotPasswordOtp && this.props.ForgotPasswordOtp !== prevProps.ForgotPasswordOtp) {
      this.props.navigate("/confirmforgotpassword");
    }
  }

  render() {
    const { forgotPasswordEmail, formError } = { ...this.props, ...this.state };

    return (
      <div className="flex w-full min-h-screen bg-white">

        {/* Left panel */}
        <div className="flex flex-col flex-1 min-h-screen px-8 py-10 lg:px-16">

          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <img src={CESSVG} alt="Logo" className="w-8 h-8" />
            <span className="font-SFPro font-bold text-gray-900 text-lg tracking-tight">CX2 AI</span>
          </div>

          {/* Form */}
          <div className="flex flex-col flex-1 justify-center">
            <div className="w-full max-w-[380px] mx-auto">

              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                <KeyRound className="w-6 h-6 text-primary-blue" />
              </div>

              <h1 className="font-SFPro font-bold text-[2rem] leading-tight text-gray-900 mb-2">
                Password reset
              </h1>
              <p className="font-inter text-sm text-gray-400 mb-8">
                We sent a 4-digit code to{" "}
                <span className="font-semibold text-gray-600">{forgotPasswordEmail}</span>
              </p>

              {formError && (
                <div className="flex items-center gap-2 px-4 py-3 mb-6 bg-red-50 border border-red-100 rounded-xl text-sm font-inter text-red-600">
                  {formError}
                </div>
              )}

              {/* OTP inputs */}
              <div className="flex justify-center gap-3 mb-8">
                {this.inputsRef.map((ref, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    ref={ref}
                    onChange={(e) => this.handleInput(e, index)}
                    onKeyDown={(e) => this.handleKeyDown(e, index)}
                    className="w-14 h-14 bg-gray-50 border border-gray-200 rounded-xl text-center text-xl font-SFPro font-bold text-gray-900 outline-none focus:bg-white focus:border-primary-blue transition-all"
                  />
                ))}
              </div>

              <button
                onClick={this.onSubmit}
                className="flex items-center justify-center w-full h-11 bg-primary-blue text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 transition-all shadow-sm"
              >
                Continue
              </button>

              <p className="mt-6 text-center font-inter text-sm text-gray-400">
                Didn't receive the code?{" "}
                <span className="text-primary-blue font-semibold cursor-pointer hover:underline">
                  Click to resend
                </span>
              </p>

              <Link
                to="/login"
                className="flex items-center justify-center gap-2 mt-6 font-inter text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </Link>
            </div>
          </div>

          <p className="font-inter text-xs text-gray-300 text-center">
            © {new Date().getFullYear()} CX2 AI. All rights reserved.
          </p>
        </div>

        {/* Right panel */}
        <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden sticky top-0 h-screen">
          <img src={ChangePasswordImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D6EFD]/60 to-gray-900/70" />
          <div className="relative z-10 flex flex-col justify-end h-full p-12">
            <div className="backdrop-blur-sm bg-white/10 border border-white/20 rounded-2xl p-6 max-w-sm">
              <p className="font-SFPro font-semibold text-white text-lg leading-snug mb-3">
                "Your account security is our top priority."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">CX</div>
                <div>
                  <p className="font-inter font-medium text-white text-sm">Security Team</p>
                  <p className="font-inter text-white/60 text-xs">Password Recovery</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  ForgotPasswordOtp: state.authentication?.ForgotPasswordOtp,
  forgotPasswordEmail: state.authentication?.forgotPasswordEmail,
});

export default withRouterHooks(
  connect(mapStateToProps, { AddForgotPasswordToOTPAction })(VerifyConfirmPassword)
);
